import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import * as api from "../api/client";
import { BRAND, BrandMark } from "../components/BrandMark";

const size = (b) => (b == null ? "—" : b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);
const when = (s) => (s ? new Date(s * 1000).toLocaleDateString() : "—");
const cap = (v) => (v == null ? "∞" : v.toLocaleString("en-IN"));

export default function OwnerPage() {
  const [owner, setOwner] = useState(undefined);
  useEffect(() => {
    api.ownerMe().then((r) => setOwner(r.owner)).catch(() => setOwner(null));
  }, []);
  if (owner === undefined) return <div className="ow-center"><Spinner animation="border" /></div>;
  return owner ? <Console owner={owner} onOut={() => setOwner(null)} /> : <OwnerLogin onIn={setOwner} />;
}

function OwnerLogin({ onIn }) {
  const [f, setF] = useState({ username: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      onIn((await api.ownerLogin(f.username, f.password)).owner);
    } catch (er) {
      setErr(er.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="ow-center">
      <form className="cx-card ow-login" onSubmit={submit}>
        <div className="ow-login-h"><BrandMark size={36} /><div><strong>{BRAND.name} platform</strong><div className="cx-muted">Owner console</div></div></div>
        {err && <div className="cx-msgbox bad">{err}</div>}
        <label className="cx-field"><span>Owner username</span>
          <input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} autoComplete="username" required /></label>
        <label className="cx-field"><span>Password</span>
          <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="current-password" required /></label>
        <button type="submit" className="cx-btn primary" disabled={busy}>{busy ? <Spinner size="sm" animation="border" /> : "Sign in"}</button>
      </form>
    </div>
  );
}

function Console({ owner, onOut }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.ownerWorkspaces());
    } catch (e) {
      setErr(e.message);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function act(fn) {
    setErr("");
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function remove(w) {
    const typed = window.prompt(`This permanently deletes “${w.name}”: its data, users, chats, models and history.\nType the workspace URL (${w.slug}) to confirm.`);
    if (typed === null) return;
    await act(() => api.ownerDeleteWorkspace(w.slug, typed));
  }

  const list = data?.workspaces || [];
  const totals = list.reduce((t, w) => ({ users: t.users + (w.usage.users || 0), bytes: t.bytes + (w.usage.bytes || 0),
    runs: t.runs + (w.usage.runs7d || 0), active: t.active + (w.status === "active" ? 1 : 0) }), { users: 0, bytes: 0, runs: 0, active: 0 });

  return (
    <div className="ow">
      <header className="ow-top">
        <div className="ow-brand"><BrandMark size={28} /><strong>{BRAND.name}</strong><span className="ow-badge">Platform owner</span></div>
        <div className="ow-who"><span className="cx-muted">{owner}</span>
          <button type="button" className="cx-btn" onClick={async () => { await api.ownerLogout(); onOut(); }}>Sign out</button></div>
      </header>
      <div className="cx-page ow-page">
        <header className="cx-head">
          <div>
            <h1 className="cx-title">Customer workspaces</h1>
            <p className="cx-sub">Every customer gets an isolated workspace: its own database schema, users, data, models and chat history. Manage plans, suspend access or remove a workspace here.</p>
          </div>
          <button type="button" className="cx-btn primary" onClick={() => setCreating(true)}>+ New workspace</button>
        </header>
        {err && <div className="cx-msgbox bad">{err}</div>}
        {!data ? <div className="cx-card cx-loading"><Spinner animation="border" size="sm" /></div> : (
          <>
            <div className="ml-stats">
              <Stat label="Workspaces" value={list.length} sub={`${totals.active} active`} primary />
              <Stat label="Users" value={totals.users.toLocaleString("en-IN")} sub="across all customers" />
              <Stat label="Questions · 7 days" value={totals.runs.toLocaleString("en-IN")} sub="chat runs answered" />
              <Stat label="Data stored" value={size(totals.bytes)} sub="customer tables" />
            </div>
            <section className="cx-card">
              <div className="cx-scroll ml-list">
                <table className="cx-table">
                  <thead><tr><th>Workspace</th><th>Plan</th><th>Users</th><th>Tables</th><th>Models</th><th>Questions 7d</th><th>Data</th><th>Last active</th><th>Status</th><th /></tr></thead>
                  <tbody>
                    {list.map((w) => (
                      <tr key={w.slug}>
                        <td>
                          <strong>{w.name}</strong>{w.isDefault && <span className="cx-pill builtin ow-main">Main</span>}
                          <div className="cx-muted"><code>{w.slug}</code> · schema <code>{w.schema}</code>{w.contact ? ` · ${w.contact}` : ""}</div>
                        </td>
                        <td>
                          <select className="ow-select" value={w.plan} disabled={w.isDefault}
                                  onChange={(e) => act(() => api.ownerUpdateWorkspace(w.slug, { plan: e.target.value }))}>
                            {Object.entries(data.plans).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                          </select>
                        </td>
                        <td>{w.usage.users ?? "—"} <span className="cx-muted">/ {cap(w.limits.users)}</span></td>
                        <td>{w.usage.tables ?? "—"}</td>
                        <td>{w.usage.models ?? "—"} <span className="cx-muted">/ {cap(w.limits.models)}</span></td>
                        <td>{w.usage.runs7d ?? "—"}</td>
                        <td>{size(w.usage.bytes)}</td>
                        <td className="cx-muted">{when(w.usage.last_active)}</td>
                        <td><span className={`cx-status ${w.status === "active" ? "ok" : "failed"}`}>{w.status}</span></td>
                        <td className="cx-actions">
                          {!w.isDefault && (w.status === "active"
                            ? <button type="button" className="cx-link danger" onClick={() => act(() => api.ownerUpdateWorkspace(w.slug, { status: "suspended" }))}>Suspend</button>
                            : <button type="button" className="cx-link" onClick={() => act(() => api.ownerUpdateWorkspace(w.slug, { status: "active" }))}>Reactivate</button>)}
                          {!w.isDefault && <button type="button" className="cx-link danger" onClick={() => remove(w)}>Delete</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <p className="cx-muted cx-note">Plans: {Object.values(data.plans).map((p) => `${p.label} — ${cap(p.users)} users, ${cap(p.models)} models, ${cap(p.jobs)} jobs`).join(" · ")}</p>
          </>
        )}
      </div>
      {creating && <CreateModal plans={data?.plans || {}} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, sub, primary }) {
  return (
    <div className={"cx-card ml-stat" + (primary ? " primary" : "")}>
      <div className="ml-stat-l">{label}</div><div className="ml-stat-v">{value}</div>{sub && <div className="cx-muted">{sub}</div>}
    </div>
  );
}

function CreateModal({ plans, onClose, onDone }) {
  const [f, setF] = useState({ workspaceName: "", workspace: "", username: "", password: "", contact: "", plan: "standard" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.ownerCreateWorkspace({ ...f, workspace: f.workspace.trim().toLowerCase() });
      onDone();
    } catch (er) {
      setErr(er.message);
      setBusy(false);
    }
  }
  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="cx-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="New workspace">
        <div className="cx-modal-h"><div><div className="cx-modal-title">New customer workspace</div>
          <div className="cx-muted">Creates an isolated schema and the customer's first admin</div></div>
          <button type="button" className="cx-x-btn" onClick={onClose} aria-label="Close">×</button></div>
        <form onSubmit={submit} className="cx-form-wrap">
          <div className="cx-modal-body">
            <div className="cx-grid">
              <label className="cx-field"><span>Company name</span><input value={f.workspaceName} onChange={set("workspaceName")} required /></label>
              <label className="cx-field"><span>Workspace URL</span><input value={f.workspace} onChange={set("workspace")} required placeholder="acme" /></label>
              <label className="cx-field"><span>Admin username</span><input value={f.username} onChange={set("username")} required autoComplete="off" /></label>
              <label className="cx-field"><span>Admin password</span><input type="password" value={f.password} onChange={set("password")} required autoComplete="new-password" /></label>
              <label className="cx-field"><span>Contact<em> · optional</em></span><input value={f.contact} onChange={set("contact")} /></label>
              <label className="cx-field"><span>Plan</span>
                <select value={f.plan} onChange={set("plan")}>{Object.entries(plans).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}</select></label>
            </div>
            {err && <div className="cx-msgbox bad">{err}</div>}
          </div>
          <div className="cx-modal-f"><button type="submit" className="cx-btn primary" disabled={busy}>{busy ? <Spinner size="sm" animation="border" /> : "Create workspace"}</button></div>
        </form>
      </div>
    </div>
  );
}
